import { Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { Footer } from '@/components/navigation/Footer';
import { useFormContext } from '@/components/react-hook-form';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useOptionalDocumentChatContext } from '@/modules/chat/contexts/DocumentChatProvider';
import { ToggleChatButton } from '@/modules/documents/EditSummaryTrowser/components/ToggleChatButton';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';

import {
  SubmitButtonType,
  useEditTestamentaryEntitySplitScreenModalContext,
} from './contexts/editTestamentaryEntitySplitScreenModal.context';
import {
  EditTestamentaryEntityFormShape,
  EditTestamentaryEntitySection,
} from './EditTestamentaryEntitySplitScreen.types';

export function EditTestamentaryEntitySplitScreenFooter() {
  const canEdit = useCanEditHouseholdData();
  const { footerRef, handleCloseModal } =
    useEditTestamentaryEntitySplitScreenModalContext();

  const chatContext = useOptionalDocumentChatContext();

  const isAIEnabled = useAICapabilitiesEnabled();
  const isDocumentChatEnabled = !!chatContext && isAIEnabled;

  return (
    <Footer
      leftAction={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => handleCloseModal(true)}
          >
            {canEdit ? 'Exit without saving' : 'Close'}
          </Button>
          {isDocumentChatEnabled && (
            <ToggleChatButton
              isChatOpen={chatContext.isChatOpen}
              onOpenChat={chatContext.openChat}
              onCloseChat={chatContext.closeChat}
            />
          )}
        </Stack>
      }
      rightAction={
        <Stack
          ref={footerRef}
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
        />
      }
    />
  );
}

interface EditTestamentaryEntitySplitScreenFooterRightActionsProps {
  onSubmit: (afterSubmitSection?: EditTestamentaryEntitySection) => void;
}

export function EditTestamentaryEntitySplitScreenFooterRightActions({
  onSubmit,
}: EditTestamentaryEntitySplitScreenFooterRightActionsProps) {
  const canEdit = useCanEditHouseholdData();
  const {
    formState: { isSubmitting },
  } = useFormContext<EditTestamentaryEntityFormShape>();

  const { clickedSubmitButton, setClickedSubmitButton } =
    useEditTestamentaryEntitySplitScreenModalContext();

  const handleButtonClick = useCallback(
    (
      button: SubmitButtonType,
      afterSubmitSection?: EditTestamentaryEntitySection
    ) => {
      setClickedSubmitButton(button);
      onSubmit(afterSubmitSection);
    },
    [onSubmit, setClickedSubmitButton]
  );

  return (
    <Stack direction="row" spacing={1}>
      {canEdit && (
        <Button
          variant="primary"
          size="sm"
          type="submit"
          onClick={() => handleButtonClick('save')}
          loading={isSubmitting && clickedSubmitButton === 'save'}
          disabled={isSubmitting}
        >
          Save & close
        </Button>
      )}
      <Stack direction="row" spacing={0.5}>
        <NextPrevTestamentaryEntitySectionFormButton
          onRequestSubmit={(dir, afterSubmitSection) =>
            handleButtonClick(dir, afterSubmitSection)
          }
          direction="prev"
        />
        <NextPrevTestamentaryEntitySectionFormButton
          onRequestSubmit={(dir, afterSubmitSection) =>
            handleButtonClick(dir, afterSubmitSection)
          }
          direction="next"
        />
      </Stack>
    </Stack>
  );
}

interface NextPrevTestamentaryEntitySectionFormButtonProps {
  onRequestSubmit: (
    dir: 'next' | 'prev',
    afterSubmitSection: EditTestamentaryEntitySection
  ) => void;
  direction: 'next' | 'prev';
}

function NextPrevTestamentaryEntitySectionFormButton({
  onRequestSubmit,
  direction,
}: NextPrevTestamentaryEntitySectionFormButtonProps) {
  const {
    formState: { isSubmitting },
  } = useFormContext<EditTestamentaryEntityFormShape>();

  const { currentSection, sectionOptions, clickedSubmitButton } =
    useEditTestamentaryEntitySplitScreenModalContext();

  const targetSection = useMemo(() => {
    if (
      !sectionOptions ||
      !Array.isArray(sectionOptions) ||
      sectionOptions.length === 0
    ) {
      return currentSection;
    }

    const currentIndex = sectionOptions.findIndex(
      (option) => option.section === currentSection
    );

    if (currentIndex === -1) {
      return currentSection;
    }

    if (direction === 'next') {
      const nextIndex = (currentIndex + 1) % sectionOptions.length;
      const nextSection = sectionOptions[nextIndex]?.section;
      return nextSection || currentSection;
    } else {
      const prevIndex =
        (currentIndex - 1 + sectionOptions.length) % sectionOptions.length;
      const prevSection = sectionOptions[prevIndex]?.section;
      return prevSection || currentSection;
    }
  }, [currentSection, direction, sectionOptions]);

  return (
    <IconButton
      variant="secondary"
      size="sm"
      type="button"
      onClick={() => onRequestSubmit(direction, targetSection)}
      loading={isSubmitting && clickedSubmitButton === direction}
      disabled={isSubmitting}
      icon={direction === 'prev' ? ChevronLeftIcon : ChevronRightIcon}
      ariaLabel={direction === 'prev' ? 'Previous' : 'Next'}
    />
  );
}
